import { clsx } from '../../../../ds/clsx';
import type { Invoice } from '../api';
import styles from './billing-ui.module.css';

const statusClass: Record<Invoice['status'], string | undefined> = {
  paid: styles.statusPaid,
  open: styles.statusOpen,
  chargeback: styles.statusChargeback,
  cancelled: styles.statusCancelled,
};

export interface InvoiceListProps {
  invoices: readonly Invoice[];
  /** `default`: the card of Einstellungen 19/28; `mobile`: the compact list of Einstellungen M9. */
  variant?: 'default' | 'mobile' | undefined;
  title?: string | undefined;
  className?: string | undefined;
}

/**
 * „Rechnungen" (Einstellungen 19/28/M9): date, number, gross amount, status pill (Bezahlt/Offen/Rückbuchung)
 * and the PDF link; an open invoice is highlighted as in Einstellungen 28. Without invoices the card says so.
 */
export function InvoiceList({
  invoices,
  variant = 'default',
  title = 'Rechnungen',
  className,
}: InvoiceListProps) {
  const mobile = variant === 'mobile';
  return (
    <section className={clsx(styles.invoices, mobile && styles.invoicesMobile, className)} aria-label={title}>
      <div className={styles.invoicesHead}>
        <h3 className={styles.invoicesTitle}>{title}</h3>
      </div>
      {invoices.length === 0 ? <div className={styles.invoicesEmpty}>Noch keine Rechnungen.</div> : null}
      {invoices.map((invoice) => (
        <div
          key={invoice.id}
          className={clsx(styles.invoiceRow, !mobile && invoice.status === 'open' && styles.invoiceRowOpen)}
        >
          {mobile ? (
            <>
              <span>{invoice.issuedOnLabel}</span>
              <span className={styles.invoiceAmount}>{invoice.amountLabel}</span>
              <span className={clsx(styles.invoiceStatus, statusClass[invoice.status])}>
                {invoice.statusLabel}
              </span>
            </>
          ) : (
            <>
              <span className={styles.invoiceDate}>{invoice.issuedOnLabel}</span>
              <span className={styles.invoiceNumber}>{invoice.number}</span>
              <span className={styles.invoiceAmount}>{invoice.amountLabel}</span>
              <span className={clsx(styles.invoiceStatus, statusClass[invoice.status])}>
                {invoice.statusLabel}
              </span>
              {invoice.pdfUrl ? (
                <a
                  className={styles.invoicePdf}
                  href={invoice.pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`PDF ${invoice.number}`}
                >
                  PDF
                </a>
              ) : (
                <span className={styles.invoicePdfMissing}>PDF</span>
              )}
            </>
          )}
        </div>
      ))}
    </section>
  );
}
